<template>
  <div id="us-orders">
    <div class="table-header">
      <v-switch
        color="primary"
        :label="includeDispatched ? 'Dispatched' : 'Awaiting Dispatch'"
        @update:model-value="includeDispatched = !includeDispatched"
        hide-details
        style="margin-right: auto"
      ></v-switch>
      <v-chip
        v-if="['sho@plykea.com', 'kath@plykea.com', 'adam@plykea.com'].includes($store.state.user.email)"
        class="text-body-2 mr-4"
        color="green"
        >{{ usOrderTally }}</v-chip
      >
      <v-text-field
        v-model="tableSearch"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        :clearable="true"
      ></v-text-field>
    </div>
    <v-data-table
      :items="filteredShipments"
      :headers="headers"
      :search="tableSearch"
      :custom-filter="customSearch"
      :loading="loading"
      :no-data-text="noDataText"
      :sort-by="[{ key: 'dateCreated', order: 'asc' }]"
      :items-per-page="50"
    >
      <template v-slot:item="props">
        <tr
          :class="{
            paused: props.item.paused
          }"
        >
          <td>{{ $utils.convertDate(props.item.dateCreated) }}</td>
          <td>
            <div class="d-flex justify-space-between">
              <div>
                <v-tooltip location="top" :open-delay="500">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-icon v-if="props.item.recut" size="small" class="mr-1" v-bind="activatorProps"
                      >mdi-puzzle</v-icon
                    >
                  </template>
                  <span>Partial recut of previous order</span>
                </v-tooltip>
                <v-tooltip location="top" :open-delay="500">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-icon
                      v-if="props.item.balanceOutstanding"
                      size="small"
                      class="mr-1"
                      color="warning"
                      v-bind="activatorProps"
                      >mdi-truck</v-icon
                    >{{ props.item.orderNumber || props.item.xeroInvoiceNumber }}
                  </template>
                  <span>Outstanding balance - don't ship</span>
                </v-tooltip>
              </div>
              <div>
                <v-tooltip location="top" :open-delay="500">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-icon
                      size="small"
                      class="mr-1"
                      @click="
                        () => {
                          selectedShipment = props.item
                          editNotesDialog = true
                        }
                      "
                      v-bind="activatorProps"
                      >mdi-comment</v-icon
                    >
                  </template>
                  <span>Order Notes</span>
                </v-tooltip>
              </div>
            </div>
          </td>
          <td>{{ props.item.customerEmail }}</td>
          <td>
            {{ props.item.customerContactDetails.firstName + ' ' + props.item.customerContactDetails.lastName }}
          </td>
          <td>
            <DatePickerPlus
              :timestamp="props.item.targetDate"
              :clearable="true"
              placeholder="Target delivery date"
              v-on:update:timestamp="
                (val) => {
                  props.item.targetDate = val
                  changed(props.item, 'targetDate')
                }
              "
            ></DatePickerPlus>
          </td>
          <td>
            <DatePickerPlus
              :timestamp="props.item.inProduction"
              :clearable="true"
              :placeholder="'Requires toolpathing'"
              v-on:update:timestamp="
                (val) => {
                  props.item.inProduction = val
                  changed(props.item, 'inProduction')
                }
              "
            ></DatePickerPlus>
          </td>
          <td>
            <DatePickerPlus
              :timestamp="props.item.dispatchEta"
              :clearable="true"
              placeholder="TBC"
              v-on:update:timestamp="
                (val) => {
                  props.item.dispatchEta = val
                  changed(props.item, 'dispatchEta')
                }
              "
            ></DatePickerPlus>
          </td>

          <td>
            <v-text-field
              class="ma-0 pa-0"
              :style="{ fontSize: '13px' }"
              placeholder="Not shipped yet"
              v-model="props.item.trackingNumber"
              @change="changed(props.item, 'trackingNumber')"
              hide-details
            ></v-text-field>
          </td>
          <td class="text-right" style="min-width: 180px">
            <!-- Pause / Play -->
            <v-tooltip v-if="props.item.paused" location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon
                  size="small"
                  class="mr-2"
                  @click="
                    () => {
                      pauseShipmentDialog = true
                      selectedShipment = props.item
                    }
                  "
                  v-bind="activatorProps"
                  >mdi-play-circle</v-icon
                >
              </template>
              <span>Unpause Shipment</span>
            </v-tooltip>
            <v-tooltip v-else location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon
                  size="small"
                  class="mr-2"
                  @click="
                    () => {
                      pauseShipmentDialog = true
                      selectedShipment = props.item
                      findQuoteFromShipment()
                    }
                  "
                  v-bind="activatorProps"
                  >mdi-pause-circle</v-icon
                >
              </template>
              <span>Pause Shipment</span>
            </v-tooltip>
            <v-tooltip location="top" :open-delay="500" v-if="$store.state.user.permissions.uk">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon size="small" class="mr-2" @click="viewQuote(props.item)" v-bind="activatorProps"
                  >mdi-clipboard-text</v-icon
                >
              </template>
              <span>Order Details</span>
            </v-tooltip>
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon size="small" class="mr-2" @click="materialView(props.item)" v-bind="activatorProps"
                  >mdi-cart</v-icon
                >
              </template>
              <span>Material Order List</span>
            </v-tooltip>
            <v-tooltip location="top" :open-delay="500" v-if="$store.state.user.permissions.us">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon class="mr-2" @click="ludicPricingView(props.item)" v-bind="activatorProps" :size="18"
                  >mdi-currency-usd</v-icon
                >
              </template>
              <span>Pricing</span>
            </v-tooltip>
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon size="small" class="mr-2" @click="ludicProductionView(props.item)" v-bind="activatorProps"
                  >mdi-wrench</v-icon
                >
              </template>
              <span>Lucid Production View</span>
            </v-tooltip>
            <v-tooltip v-if="!includeDispatched" location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon
                  :disabled="!props.item.dispatchEta"
                  size="small"
                  class="mr-2"
                  @click="
                    () => {
                      selectedOrder = props.item
                      shippedDialog = true
                    }
                  "
                  v-bind="activatorProps"
                  >mdi-truck</v-icon
                >
              </template>
              <span>Mark order as shipped</span>
            </v-tooltip>
            <v-tooltip v-else location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon
                  size="small"
                  class="mr-2"
                  @click="
                    () => {
                      selectedOrder = props.item
                      unShippedDialog = true
                    }
                  "
                  v-bind="activatorProps"
                  >mdi-truck</v-icon
                >
              </template>
              <span>Mark as NOT shipped</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Component Overlay Dialog -->
    <v-dialog v-model="dialogVisible" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar color="primary" class="no-print elevation-0">
          <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="
              () => {
                dialogVisible = false
                overlayComponent = null
              }
            "
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <component
          :is="overlayComponent"
          :shipment="selectedOrder"
          :quoteID="selectedOrder.quoteID"
          :versionID="selectedOrder.versionID"
          :orderID="selectedOrder.id"
        ></component>
      </v-card>
    </v-dialog>

    <v-dialog v-model="shippedDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6"> Mark as shipped </v-card-title>
        <v-card-text> Are you sure you want to mark this order as shipped? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="shippedDialog = false">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="shipped(true)">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unShippedDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6"> Mark as NOT shipped </v-card-title>
        <v-card-text> Are you sure you want to move the order back into production? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="unShippedDialog = false">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="shipped(false)">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Pause -->
    <v-dialog v-model="pauseShipmentDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">
          {{ selectedShipment && selectedShipment.paused ? 'Unpause Shipment' : 'Pause Shipment' }}
        </v-card-title>
        <v-card-text v-if="selectedShipment && !selectedShipment.paused" class="pt-6">
          <div class="text-h6">Why?</div>
          <v-form ref="pauseForm">
            <v-textarea
              v-model="pauseForm.why"
              :rules="[(v) => !!v || 'A reason is required']"
              variant="outlined"
              placeholder="Please leave a brief note to explain why the order is being paused"
            ></v-textarea>
            <v-checkbox class="text-h6" v-model="pauseForm.changesCheck" label="Changes Have Been Made"></v-checkbox>
            <p v-if="pauseForm.changesCheck" class="text-caption text-red">
              Please state changes concisely.<br />
              Production notes will be generated from the latest pause; gather a full list of changes made.<br />
              Edits to the change statement can be made.
            </p>
            <div class="text-h6" v-if="pauseForm.changesCheck">Changes:</div>
            <v-textarea
              v-if="pauseForm.changesCheck"
              v-model="pauseForm.changes"
              :rules="[(v) => !!(v && v.trim()) || 'Describe the changes — this becomes the production notes']"
              variant="outlined"
              placeholder="List the changes in the format: [material] - [unit numbers] - [state changes]"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card
          v-else
          id="tasks-dialog"
          @input="
            () => {
              !pauseShipmentDialog
              selectedShipment = {}
            }
          "
        >
          <v-card-title class="bg-primary text-white text-h6"> Pause Log </v-card-title>
          <v-card-text>
            <div v-for="(item, key) of this.selectedShipment.pauseLog" :key="key" class="pause-log-item mt-4">
              <div class="text-caption d-flex">
                <strong>
                  {{ `${item.author} - ${$moment.unix(item.date.seconds).format('Do MMM YYYY')}` }}
                </strong>
                <v-spacer></v-spacer>
                <v-icon
                  class="mr-1"
                  @click="
                    () => {
                      editPauseLogDialog = true
                      selectedPauseLog = item
                      selectedPauseLogKey = key
                      findQuoteFromShipment()
                    }
                  "
                  >mdi-pencil</v-icon
                >
                <v-icon
                  class="mr-1"
                  color="red"
                  @click="
                    () => {
                      deletePauseLogDialog = true
                      selectedPauseLog = item
                      selectedPauseLogKey = key
                      findQuoteFromShipment()
                    }
                  "
                  >mdi-delete
                </v-icon>
              </div>
              <div class="text-caption pt-2">Why?</div>
              <div class="text-body-1">{{ item.why }}</div>
              <div class="text-caption pt-2">Changes?</div>
              <div class="text-body-1">{{ item.changes || 'None' }}</div>
            </div>
          </v-card-text>
        </v-card>
        <div class="d-flex flex-column justify-end">
          <v-alert v-if="selectedShipment && selectedShipment.paused" type="warning" class="d-block">
            All work in production will restart if you unpause this shipment! <br />
            Please check that the latest change statement is accurate as this will be generated as production notes.
          </v-alert>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="pauseShipmentDialog = false">Cancel</v-btn>
            <v-btn
              color="warning"
              variant="flat"
              :disabled="pauseBlocked"
              @click="toggleShipmentPause(selectedShipment)"
              >{{ selectedShipment && selectedShipment.paused ? 'Unpause' : 'Pause' }}</v-btn
            >
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>

    <!-- EDIT PAUSE LOG -->
    <v-dialog v-model="editPauseLogDialog" width="500" @update:model-value="updatePauseLog(selectedPauseLogKey)">
      <v-card>
        <v-card-title class="bg-primary text-h6 text-white">Edit Pause Log</v-card-title>
        <v-card-text class="mt-6">
          <div class="text-caption pt-2">Why</div>
          <div class="text-body-1 mb-2">{{ selectedPauseLog.why }}</div>
          <div class="text-caption pt-2">Edit Change Statement</div>
          <v-textarea v-model="selectedPauseLog.changes"></v-textarea>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- DELETE PAUSE LOG -->
    <v-dialog v-model="deletePauseLogDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-h6 text-white">Delete Pause Log</v-card-title>
        <v-card-text class="mt-6">
          <div class="text-caption pt-2">Why</div>
          <div class="text-body-1 mb-2">{{ selectedPauseLog.why }}</div>
          <div class="text-caption pt-2">Edit Change Statement</div>
          <div class="text-body-1 mb-2">{{ selectedPauseLog.changes }}</div>
        </v-card-text>
        <v-alert type="warning" class="pt-6">
          Are you sure you want to delete this pause log? <br />
          Remember the previous pause log will then be used for production notes.
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="deletePauseLogDialog = false">Cancel</v-btn>
          <v-btn
            color="warning"
            variant="flat"
            @click="deletePauseLog(selectedPauseLogKey)"
            :disabled="deletingPauseLog"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notes dialog -->
    <v-dialog
      v-if="editNotesDialog"
      v-model="editNotesDialog"
      width="500"
      @update:model-value="
        () => {
          selectedShipment = {}
          selectedQuote = null
          selectedVersionIndex = null
        }
      "
    >
      <v-card>
        <v-card-text class="pt-3">
          <v-textarea variant="outlined" label="Order notes" hide-details v-model="selectedShipment.notes"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="editNotesDialog = false">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="updateNotes">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import QuoteEdit from '@/views/QuoteEdit'
import MaterialOrderView from '@/components/MaterialOrderView'
import LucidPricingView from '@/components/us/LucidPricingView'
import DatePickerPlus from '@/components/tables/DatePickerPlus'
import { auth, db } from '@/js/db'
import { arrayUnion, collection, doc, getDoc, query, updateDoc, where } from 'firebase/firestore'

export default {
  name: 'UsProduction',
  components: { QuoteEdit, MaterialOrderView, LucidPricingView, DatePickerPlus },
  data() {
    return {
      includeDispatched: false,
      tableSearch: '',
      orders: [],
      selectedOrder: {},
      loading: true,
      cloning: false,
      editNotesDialog: false,
      headers: [
        {
          title: 'Date',
          sortable: true,
          key: 'dateCreated'
        },
        {
          title: 'Order / Inv #',
          sortable: true,
          key: 'invoiceNumber'
        },
        {
          title: 'Customer Email',
          sortable: false,
          key: 'customerEmail'
        },
        {
          title: 'Customer Name',
          sortable: false,
          key: 'customerName'
        },
        {
          title: 'Target Date',
          sortable: true,
          key: 'targetDate'
        },
        {
          title: 'Toolpathed',
          sortable: true,
          key: 'inProduction'
        },
        {
          title: 'Dispatch ETA',
          sortable: true,
          key: 'dispatchEta'
        },
        {
          title: 'Tracking Link',
          sortable: false,
          key: 'trackingNumber'
        },
        {
          title: 'Actions',
          sortable: false,
          align: 'end',
          width: 160,
          key: null
        }
      ],
      overlayComponent: null,
      dialogTitle: null,
      dialogVisible: false,
      shippedDialog: false,
      unShippedDialog: false,
      pauseShipmentDialog: false,
      selectedQuote: null,
      selectedVersionIndex: null,
      selectedShipment: {},
      pauseForm: {
        why: null,
        changesCheck: null,
        changes: null
      },
      editPauseLogDialog: false,
      deletePauseLogDialog: false,
      deletingPauseLog: false,
      selectedPauseLog: {},
      selectedPauseLogKey: null
    }
  },
  computed: {
    pauseBlocked() {
      if (!this.selectedShipment || this.selectedShipment.paused) return false
      if (!this.pauseForm.changesCheck) return false
      return !(this.pauseForm.changes && this.pauseForm.changes.trim())
    },
    noDataText() {
      return this.loading ? '' : 'Nothing to display'
    },
    usOrderTally() {
      if (!this.includeDispatched) {
        let usProductionOrders = this.orders.filter((order) => {
          return order.shipped == false && order.foc == false && order.region == 'us'
        })
        let totalSum = 0
        for (const order of usProductionOrders) {
          totalSum += order?.total
        }
        return 'Orders Totals = $' + this.$numeral(totalSum).format('0,0.00')
      } else {
        let usDispatchedOrders = this.orders.filter((order) => {
          return order.shipped == true && order.foc == false && order.region == 'us'
        })
        let totalSum = 0
        for (const order of usDispatchedOrders) {
          totalSum += order?.total
        }
        return 'Orders Totals = $' + this.$numeral(totalSum).format('0,0.00')
      }
    },
    filteredShipments() {
      if (!this.includeDispatched) {
        return this.orders.filter((order) => {
          return order.shipped == false && (order.foc || order.numPayments > 0)
        })
      } else {
        return this.orders.filter((order) => {
          return order.shipped == true
        })
      }
    }
  },
  methods: {
    changed(order, attribute) {
      // update in firebase
      updateDoc(doc(db, 'orders', order.id), {
        [attribute]: order[attribute]
      })
        .then(() => {
          this.$store.commit('openSnackbar', {
            snackbarText: 'Order Updated 🙌'
          })
        })
        .catch((err) => {
          console.error(err)
        })
    },
    customSearch(value, search, item) {
      // V3 hands the internal item ({ raw, columns, key, value }), not the row — .raw is V2's `item`.
      return Object.values(item.raw).some((v) => v && v.toString().toLowerCase().includes(search))
    },
    shipped(isIt) {
      updateDoc(doc(db, 'orders', this.selectedOrder.id), {
        shipped: isIt
      })
        .then(() => {
          this.$store.commit('openSnackbar', {
            snackbarText: 'Order Updated 🙌'
          })
          this.shippedDialog = false
          this.unShippedDialog = false
        })
        .catch((err) => {
          console.error(err)
        })
    },
    formatLineItems(a) {
      var s = ''
      for (var i = 0; i < a.length; i++) {
        s += a[i]
        if (i < a.length - 1) s += ', '
      }
      return s
    },
    ludicPricingView(order) {
      this.dialogVisible = true
      this.selectedOrder = order
      this.dialogTitle = 'Lucid Pricing - ' + this.selectedOrder.customerEmail
      this.overlayComponent = 'LucidPricingView'
    },
    materialView(order) {
      this.dialogVisible = true
      this.selectedOrder = order
      this.dialogTitle = 'Material Order List'
      this.overlayComponent = 'MaterialOrderView'
    },
    ludicProductionView(order) {
      var routeData = this.$router.resolve({
        name: 'lucidProductionView',
        params: { orderID: order.id }
      })
      window.open(routeData.href, '_blank')
    },
    viewQuote(order) {
      this.dialogVisible = true
      this.selectedOrder = order
      this.dialogTitle = order.customerEmail
      this.overlayComponent = 'QuoteEdit'
    },
    async toggleShipmentPause(shipment) {
      let pauseLog
      let updateObj = {
        paused: !this.selectedShipment.paused
      }
      if (!this.selectedShipment.paused) {
        // paused state
        if (!(await this.$utils.validateForm(this.$refs.pauseForm))) {
          return
        } else {
          if (!this.selectedShipment.pauseLog) {
            pauseLog = [
              {
                why: this.pauseForm.why,
                changes: this.pauseForm.changes ? this.pauseForm.changes : 'None',
                date: this.$utils.tsNow(),
                author: auth.currentUser.email
              }
            ]
          } else {
            pauseLog = arrayUnion({
              why: this.pauseForm.why,
              changes: this.pauseForm.changes ? this.pauseForm.changes : 'None',
              date: this.$utils.tsNow(),
              author: auth.currentUser.email
            })
          }

          // save change statement (will always be latest pause log) in selectedQuote
          this.selectedQuote.versions[this.selectedVersionIndex].changeStatement = this.pauseForm.changes
            ? this.pauseForm.changes
            : ''

          // then update to quotes db
          updateDoc(doc(db, 'quotes', this.selectedShipment.quoteID), {
            versions: this.selectedQuote.versions
          }).then(() => {
            this.$store.commit('openSnackbar', {
              snackbarText: '🗒️ Changes added as Prod Notes!'
            })
            this.selectedQuote = null
            this.selectedVersionIndex = null
          })
        }
        updateObj.pauseLog = pauseLog
      }

      this.pauseShipmentDialog = false

      this.pauseForm.why = null
      this.pauseForm.changes = null
      if (this.pauseForm.changesCheck) this.pauseForm.changesCheck = null

      updateDoc(doc(db, 'orders', shipment.id), updateObj)
        .then(() => {
          this.$store.commit('openSnackbar', { snackbarText: 'Order Updated' })
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })
    },
    findQuoteFromShipment() {
      getDoc(doc(db, 'quotes', this.selectedShipment.quoteID)).then((quoteRef) => {
        this.selectedQuote = quoteRef.data()

        for (var i = 0; i < this.selectedQuote.versions.length; i++) {
          if (this.selectedQuote.versions[i].id == this.selectedShipment.versionID) {
            this.selectedVersionIndex = i
          }
        }
      })
    },
    updatePauseLog(key) {
      let updateObj = {
        paused: this.selectedShipment.paused
      }

      updateObj.pauseLog = this.selectedShipment.pauseLog

      this.editPauseLogDialog = false
      this.selectedPauseLog = {}

      updateDoc(doc(db, 'orders', this.selectedShipment.id), updateObj)
        .then(() => {
          this.$store.commit('openSnackbar', { snackbarText: 'Pause Log Updated' })
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })

      // Trying to find order with same quote ID from selected shipment
      if (key && key == this.selectedShipment.pauseLog.length - 1) {
        // add latest pausedLog to selectedQuote as changeStatement
        this.selectedQuote.versions[this.selectedVersionIndex].changeStatement =
          this.selectedShipment.pauseLog[key].changes

        // then update to orders db to generate prod notes
        updateDoc(doc(db, 'quotes', this.selectedShipment.quoteID), {
          versions: this.selectedQuote.versions
        }).then(() => {
          this.$store.commit('openSnackbar', {
            snackbarText: '🗒️ Changes added as Prod Notes!'
          })
          this.selectedQuote = null
          this.selectedVersionIndex = null
        })
      }
    },
    deletePauseLog(key) {
      this.deletePauseLogDialog = false
      this.deletingPauseLog = true

      let generateProdNote
      if (key == this.selectedShipment.pauseLog.length - 1 && key != 0) generateProdNote = true

      let clearChangeStatement
      if (key == 0) clearChangeStatement = true

      // then will delete that log!
      this.selectedShipment.pauseLog.splice(key, 1)

      if (generateProdNote) {
        let latest = this.selectedShipment.pauseLog.length - 1
        // add latest pausedLog to selectedQuote as changeStatement
        this.selectedQuote.versions[this.selectedVersionIndex].changeStatement =
          this.selectedShipment.pauseLog[latest].changes
      } else if (clearChangeStatement) {
        this.selectedQuote.versions[this.selectedVersionIndex].changeStatement = ''
      }

      // then update to quotes db to generate prod notes
      updateDoc(doc(db, 'quotes', this.selectedShipment.quoteID), {
        versions: this.selectedQuote.versions
      }).then(() => {
        this.$store.commit('openSnackbar', {
          snackbarText: '🗒️ Changes added as Prod Notes!'
        })
      })

      let updateObj = {
        paused: this.selectedShipment.paused
      }
      updateObj.pauseLog = this.selectedShipment.pauseLog
      updateDoc(doc(db, 'orders', this.selectedShipment.id), updateObj)
        .then(() => {
          this.$store.commit('openSnackbar', { snackbarText: 'Pause Log Updated' })
          this.deletingPauseLog = false
          this.selectedQuote = null
          this.selectedVersionIndex = null
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })
    },
    async updateNotes() {
      this.editNotesDialog = false

      updateDoc(doc(db, 'orders', this.selectedShipment.id), {
        notes: this.selectedShipment.notes
      })
      this.$store.commit('openSnackbar', {
        snackbarText: 'Notes Updated 🙌'
      })
    }
  },
  async created() {
    await this.$live('orders', query(collection(db, 'orders'), where('region', '==', 'us')))
    this.loading = false
  }
}
</script>
<style lang="scss" scoped>
#us-orders {
  width: 100%;

  a {
    text-decoration: none;
  }
  table {
    border-top: solid 1px #e0e0e0;
    border-bottom: solid 1px #e0e0e0;
    th,
    td {
      padding: 0 10px;
      border-right: solid 1px #e0e0e0;
      &:last-of-type {
        border-right: none;
      }
    }
  }
}

.pause-log-item {
  padding: 8px;
  border: solid 1px grey;
}
</style>
